import { useCallback, useEffect, useMemo, useState } from 'react'
import { useUserRole } from '@/hooks/useUserRole'
import { getAuthToken } from '@/lib/api/apiClient'
import {
  buildResidentExportUrl,
  fetchCompanies,
  fetchProperties,
  fetchResidentList,
  fetchSalesUsers,
  fetchStatusList,
} from '../services/residentApi'
import type {
  ResidentListFilters,
  ResidentListLabelMaps,
  ResidentListQuery,
  ResidentListRow,
  ResidentStatusListMappings,
  SelectOption,
} from '../types'
import {
  constantsToSelectOptions,
  EMPTY_RESIDENT_LIST_LABEL_MAPS,
  EMPTY_RESIDENT_STATUS_LIST_MAPPINGS,
  payStatusFilterSelectOptions,
  parseStatusListLabelMaps,
  parseStatusListMappings,
} from '../utils/residentListConstants'
import {
  clearResidentListSessionFilter,
  readResidentListSessionFilter,
  writeResidentListSessionFilter,
} from '../utils/residentSessionFilter'
import {
  buildInitialResidentListQuery,
  buildResidentListQuery,
} from '../utils/residentQuery'

export function useResidentList() {
  const role = useUserRole()
  const [query, setQuery] = useState<ResidentListQuery>(() =>
    buildInitialResidentListQuery(role, readResidentListSessionFilter()),
  )
  const [rows, setRows] = useState<ResidentListRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({ current: 1, last: 1, total: 0 })
  const [statusLabelMaps, setStatusLabelMaps] = useState<ResidentListLabelMaps>(
    EMPTY_RESIDENT_LIST_LABEL_MAPS,
  )
  const [statusMappings, setStatusMappings] = useState<ResidentStatusListMappings>(
    EMPTY_RESIDENT_STATUS_LIST_MAPPINGS,
  )
  const [companyOptions, setCompanyOptions] = useState<SelectOption[]>(() =>
    query.companyId && query.company_name ? [{ value: query.companyId, label: query.company_name }] : [],
  )
  const [propertyOptions, setPropertyOptions] = useState<SelectOption[]>(() =>
    query.propertyId && query.property_name ? [{ value: query.propertyId, label: query.property_name }] : [],
  )
  const [salesOptions, setSalesOptions] = useState<SelectOption[]>([])

  const load = useCallback(async (nextQuery: ResidentListQuery) => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetchResidentList(nextQuery)
      setRows(response.data || [])
      setPagination({
        current: response.current ?? nextQuery.page ?? 1,
        last: response.last ?? 1,
        total: response.total ?? 0,
      })
    } catch {
      setRows([])
      setPagination({ current: 1, last: 1, total: 0 })
      setError(null)
    } finally {
      setLoading(false)
    }
  }, [])

  const persistAndLoad = useCallback(
    (next: ResidentListQuery) => {
      setQuery(next)
      writeResidentListSessionFilter(next)
      void load(next)
    },
    [load],
  )

  const applyFilters = useCallback(
    (filters: Partial<ResidentListFilters>) => {
      const next = buildResidentListQuery(query, filters)
      persistAndLoad(next)
    },
    [persistAndLoad, query],
  )

  const resetFilters = useCallback(() => {
    clearResidentListSessionFilter()
    const next = buildInitialResidentListQuery(role)
    setQuery(next)
    writeResidentListSessionFilter(next)
    void load(next)
  }, [load, role])

  const changePage = useCallback(
    (page: number) => {
      persistAndLoad({ ...query, page })
    },
    [persistAndLoad, query],
  )

  const changePerPage = useCallback(
    (perPage: number) => {
      persistAndLoad({ ...query, page: 1, perPage })
    },
    [persistAndLoad, query],
  )

  const sort = useCallback(
    (orderBy: string) => {
      persistAndLoad({
        ...query,
        page: 1,
        orderBy,
        sortBy: query.sortBy === 'asc' ? 'desc' : 'asc',
      })
    },
    [persistAndLoad, query],
  )

  const refetch = useCallback(() => {
    void load(query)
  }, [load, query])

  const refetchFromStart = useCallback(() => {
    persistAndLoad({ ...query, page: 1 })
  }, [persistAndLoad, query])

  const searchCompanies = useCallback(async (search?: string, isPaste = false) => {
    if (!isPaste && typeof search === 'string' && search.length < 2) return [] as SelectOption[]
    const options = await fetchCompanies(search)
    setCompanyOptions(options)
    return options
  }, [])

  const searchProperties = useCallback(
    async (search?: string, overrideCompanyId?: string | number, isPaste = false) => {
      if (!isPaste && typeof search === 'string' && search.length % 2 !== 0) return [] as SelectOption[]
      const rawCompanyId = overrideCompanyId ?? query.companyId
      const companyToUse = rawCompanyId !== undefined && rawCompanyId !== null ? String(rawCompanyId) : undefined
      const options = await fetchProperties(search, companyToUse)
      setPropertyOptions(options)
      return options
    },
    [query.companyId],
  )

  useEffect(() => {
    void fetchStatusList()
      .then((statusList) => {
        setStatusLabelMaps(parseStatusListLabelMaps(statusList))
        setStatusMappings(parseStatusListMappings(statusList))
      })
      .catch(() => {
        setStatusLabelMaps(EMPTY_RESIDENT_LIST_LABEL_MAPS)
        setStatusMappings(EMPTY_RESIDENT_STATUS_LIST_MAPPINGS)
      })
  }, [])

  useEffect(() => {
    const sessionFilter = readResidentListSessionFilter()
    let merged: typeof sessionFilter = sessionFilter
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search)
      const applicantIdFromUrl = searchParams.get('applicant_id')?.trim()
      const pastCustomerFromUrl = searchParams.get('past_customer')?.trim()
      if (applicantIdFromUrl || pastCustomerFromUrl) {
        merged = {
          ...sessionFilter,
          ...(applicantIdFromUrl ? { q: applicantIdFromUrl } : {}),
          ...(pastCustomerFromUrl ? { past_customer: pastCustomerFromUrl } : {}),
        }
      }
    }
    const next = buildInitialResidentListQuery(role, merged)
    setQuery(next)
    writeResidentListSessionFilter(next)
    void load(next)
    void fetchSalesUsers().then(setSalesOptions).catch(() => setSalesOptions([]))
    if (!role.companyId) void searchCompanies()
    if (!role.propertyId) void searchProperties()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount bootstrap only
  }, [])

  const payStatusOptions = useMemo(
    () => payStatusFilterSelectOptions(statusLabelMaps.pay_status),
    [statusLabelMaps.pay_status],
  )
  const residentRentalStatusOptions = useMemo(
    () => constantsToSelectOptions(statusLabelMaps.resident_rental_status),
    [statusLabelMaps.resident_rental_status],
  )
  const residentRentalProgressOptions = useMemo(
    () => constantsToSelectOptions(statusLabelMaps.resident_rental_progress),
    [statusLabelMaps.resident_rental_progress],
  )
  const reimbursementTypeOptions = useMemo(
    () => constantsToSelectOptions(statusLabelMaps.reimbursement_type),
    [statusLabelMaps.reimbursement_type],
  )
  const reimbursementRentalStatusOptions = useMemo(
    () => constantsToSelectOptions(statusLabelMaps.reimbursement_rental_status),
    [statusLabelMaps.reimbursement_rental_status],
  )
  const reimbursementRentalProgressOptions = useMemo(
    () => constantsToSelectOptions(statusLabelMaps.reimbursement_rental_progress),
    [statusLabelMaps.reimbursement_rental_progress],
  )

  const openExportInNewTab = useCallback(() => {
    const url = buildResidentExportUrl(query, getAuthToken())
    window.open(url, '_blank', 'noopener,noreferrer')
  }, [query])

  return {
    rows,
    loading,
    error,
    query,
    pagination,
    statusLabelMaps,
    statusMappings,
    companyOptions,
    propertyOptions,
    salesOptions,
    payStatusOptions,
    residentRentalStatusOptions,
    residentRentalProgressOptions,
    reimbursementTypeOptions,
    reimbursementRentalStatusOptions,
    reimbursementRentalProgressOptions,
    isCompanyLocked: Boolean(role.companyId),
    isPropertyLocked: Boolean(role.propertyId),
    openExportInNewTab,
    applyFilters,
    resetFilters,
    changePage,
    changePerPage,
    sort,
    refetch,
    refetchFromStart,
    searchCompanies,
    searchProperties,
  }
}
